import Button, { ButtonVariants } from '@gym-atoms/Button/Button';
import { DateTimePickerChangeEvent } from '@gym-atoms/DateTimePicker/DateTimePicker';
import Loader from '@gym-atoms/Loader/Loader';
import ActivityFeed from '@gym-molecules/ActivityFeed/ActivityFeed';
import ActivityFeedFilters from '@gym-molecules/ActivityFeedFilters/ActivityFeedFilters';
import EmptyState from '@gym-molecules/EmptyState/EmptyState';
import { Activity } from '@gym-particles/types/ActivityFeed';
import { UserRole } from '@gym-particles/types/User';
import { fetchAccessPoints } from '@gym-redux/slices/accessPointsSlice';
import { fetchActivity } from '@gym-redux/slices/activitySlice';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const pageSize = 15;
const accessDoorStageId = 3;

enum AccessEventFilter {
  ALL = 'all',
  GRANTED = 'GymKeyAccessGranted',
  DENIED = 'GymKeyAccessDenied'
}

const isAccessEvent = (activity: Activity) =>
  activity.eventDescription === AccessEventFilter.GRANTED ||
  activity.eventDescription === AccessEventFilter.DENIED;

const parseDoorIds = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter((doorId): doorId is number => typeof doorId === 'number')
    : [];

type CsvHeaders = {
  date: string;
  time: string;
  result: string;
  firstName: string;
  lastName: string;
  email: string;
  door: string;
  location: string;
};

type CsvResults = {
  granted: string;
  denied: string;
};

const escapeCsvValue = (value: string | number) => {
  const text = String(value);
  const safeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;

  return `"${safeText.replace(/"/g, '""')}"`;
};

const toCsv = (activities: Activity[], headers: CsvHeaders, results: CsvResults) => {
  const headerRow = Object.values(headers).map(escapeCsvValue).join(',');
  const activityRows = activities.map((activity) =>
    [
      activity.activityDate,
      activity.createdTime,
      activity.eventDescription === AccessEventFilter.GRANTED ? results.granted : results.denied,
      activity.firstName,
      activity.lastName,
      activity.memberEmail,
      activity.locationAccessPointName || '',
      activity.locationName
    ]
      .map(escapeCsvValue)
      .join(',')
  );

  return [headerRow, ...activityRows].join('\r\n');
};

const downloadCsv = (csv: string, fileName: string) => {
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const filterAccessEvents = (
  activities: Activity[],
  eventFilter: AccessEventFilter,
  selectedDoorIds: number[],
  availableDoorIds: number[]
) => {
  const allDoorsSelected =
    availableDoorIds.length > 0 && selectedDoorIds.length === availableDoorIds.length;
  const shouldFilterByDoor = selectedDoorIds.length > 0 && !allDoorsSelected;

  return activities.filter(
    (activity) =>
      isAccessEvent(activity) &&
      (eventFilter === AccessEventFilter.ALL || activity.eventDescription === eventFilter) &&
      (!shouldFilterByDoor ||
        (activity.locationAccessPointId !== undefined &&
          selectedDoorIds.includes(activity.locationAccessPointId)))
  );
};

const GymDashboardActivity = (props: GymDashboardActivityProps) => {
  const dispatch = useAppDispatch();
  const [offset, setOffset] = useState(0);
  const { t } = useTranslation();
  const userId = useAppSelector((state) => state.user.userId);
  const [startingDate, setStartingDate] = useState<Date>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  );
  const [endingDate, setEndingDate] = useState<Date>(new Date());
  const [selectedDoorIds, setSelectedDoorIds] = useState<number[]>([]);
  const [eventFilter, setEventFilter] = useState<AccessEventFilter>(AccessEventFilter.ALL);
  const [refresh, setRefresh] = useState(false);
  const isActivityLoading = useAppSelector((state) => state.activity.isActivityLoading);
  const totalActivityCount =
    useAppSelector(
      (state) =>
        state.activity.items.find((g) => g.siteLocationId === props.gymId)?.activityItems
          .totalRecords
    ) || 0;
  const userRole = useAppSelector((state) => state.user.userRole);
  const accessPoints = useAppSelector(
    (state) =>
      state.accessPoint.items.find((item) => item.siteLocationId === props.gymId)?.items || []
  );
  const doors = useMemo(
    () => accessPoints.filter((accessPoint) => accessPoint.accessPointType === 'door'),
    [accessPoints]
  );
  const doorIds = useMemo(() => doors.map((door) => door.id), [doors]);
  const doorOptions = useMemo(
    () => doors.map((door) => ({ label: door.doorName, value: door.id })),
    [doors]
  );

  useEffect(() => {
    dispatch(
      fetchActivity({
        userId: userId,
        siteLocationId: props.gymId,
        pageSize: pageSize,
        offset: offset,
        dateFrom: `${dayjs(startingDate).format('YYYY-MM-DD')} 00:00:00`,
        dateTo: `${dayjs(endingDate).format('YYYY-MM-DD')} 23:59:59`,
        stageId: accessDoorStageId,
        eventType: AccessEventFilter.ALL,
        platformAdmin: userRole === UserRole.SYSTEM_ADMIN
      })
    );
  }, [props.gymId, userId, offset, startingDate, endingDate, refresh, userRole, dispatch]);

  useEffect(() => {
    dispatch(fetchAccessPoints({ siteLocationId: props.gymId }));
    setSelectedDoorIds([]);
  }, [dispatch, props.gymId]);

  const activities = useAppSelector(
    (state) =>
      state.activity.items.find((e) => e.siteLocationId === props.gymId)?.activityItems.items
  );
  const visibleActivities = useMemo(
    () => filterAccessEvents(activities || [], eventFilter, selectedDoorIds, doorIds),
    [activities, doorIds, eventFilter, selectedDoorIds]
  );

  const allActivityChangeHandler = () => {
    setOffset(0);
    setEventFilter(AccessEventFilter.ALL);
  };
  const errorActivityChangeHandler = () => {
    setOffset(0);
    setEventFilter(AccessEventFilter.DENIED);
  };
  const accessActivityChangeHandler = () => {
    setOffset(0);
    setEventFilter(AccessEventFilter.GRANTED);
  };

  const startDateChangeHandler = (e: DateTimePickerChangeEvent) => {
    setOffset(0);
    setStartingDate(e.target.value);
  };
  const endDateChangeHandler = (e: DateTimePickerChangeEvent) => {
    setOffset(0);
    setEndingDate(e.target.value);
  };

  const exportVisibleActivities = () => {
    const csv = toCsv(
      visibleActivities,
      {
        date: t('GYM_DASHBOARD.CSV_HEADERS.DATE'),
        time: t('GYM_DASHBOARD.CSV_HEADERS.TIME'),
        result: t('GYM_DASHBOARD.CSV_HEADERS.RESULT'),
        firstName: t('GYM_DASHBOARD.CSV_HEADERS.FIRST_NAME'),
        lastName: t('GYM_DASHBOARD.CSV_HEADERS.LAST_NAME'),
        email: t('GYM_DASHBOARD.CSV_HEADERS.EMAIL'),
        door: t('GYM_DASHBOARD.CSV_HEADERS.DOOR'),
        location: t('GYM_DASHBOARD.CSV_HEADERS.LOCATION')
      },
      {
        granted: t('GYM_DASHBOARD.CSV_RESULTS.GRANTED'),
        denied: t('GYM_DASHBOARD.CSV_RESULTS.DENIED')
      }
    );
    const dateFrom = dayjs(startingDate).format('YYYY-MM-DD');
    const dateTo = dayjs(endingDate).format('YYYY-MM-DD');

    downloadCsv(csv, `access-events-${dateFrom}-${dateTo}.csv`);
  };

  return (
    <>
      {isActivityLoading ? (
        <Loader shape="activity" />
      ) : (
        <>
          <div>
            <ActivityFeedFilters
              allActivity={eventFilter === AccessEventFilter.ALL}
              errorActivity={eventFilter === AccessEventFilter.DENIED}
              accessActivity={eventFilter === AccessEventFilter.GRANTED}
              dateFrom={startingDate}
              dateTo={endingDate}
              allActivityOnChange={allActivityChangeHandler}
              errorActivityOnChange={errorActivityChangeHandler}
              accessActivityOnChange={accessActivityChangeHandler}
              dateFromOnChange={startDateChangeHandler}
              dateToOnChange={endDateChangeHandler}
              dropdownOptions={doorOptions}
              selectedValue={selectedDoorIds}
              dropdownOnChange={(e) => {
                setOffset(0);
                setSelectedDoorIds(parseDoorIds(e.value));
              }}
              dropdownPlaceholder={t('GYM_DASHBOARD.ALL_DOORS')}
              dropdownChips={true}
            />
          </div>
          <div className="d-flex justify-content-end mb10">
            <Button
              label={t('GYM_DASHBOARD.EXPORT_CSV')}
              icon="pi-download"
              iconPos="left"
              disabled={visibleActivities.length === 0}
              onClick={exportVisibleActivities}
            />
          </div>
          <div className="activty-feed">
            <ActivityFeed
              activities={visibleActivities}
              stage={'siteLocation'}
              refreshCallBack={() => setRefresh((currentRefresh) => !currentRefresh)}
              countChange={props.countChange}
              isCountChanged={props.isCountChanged}></ActivityFeed>
          </div>
          <div className="text-center">
            {visibleActivities.length === 0 && (
              <EmptyState textItems={[t('GYM_DASHBOARD_PAGE.EMPTY_STATE_TEXT')]} />
            )}
            {totalActivityCount > 0 && (
              <div className="d-flex button-view-all justify-content-center">
                <div className="mr15">
                  <Button
                    data-cy={'dashboard-activity-previous-btn'}
                    variant={ButtonVariants.textonlyslim}
                    label={t('GYM_DASHBOARD.VIEW_PREVIOUS_ACTIVITY')}
                    icon="pi-angle-left"
                    iconPos="left"
                    disabled={offset === 0}
                    onClick={() => setOffset((currentOffset) => currentOffset - pageSize)}></Button>
                </div>
                <div>
                  <Button
                    data-cy={'dashboard-activity-next-btn'}
                    variant={ButtonVariants.textonlyslim}
                    label={t('GYM_DASHBOARD.VIEW_NEXT_ACTIVITY')}
                    icon="pi-angle-right"
                    iconPos="right"
                    disabled={offset + pageSize >= totalActivityCount}
                    onClick={() => setOffset((currentOffset) => currentOffset + pageSize)}></Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export interface GymDashboardActivityProps {
  gymId: number;
  countChange?: (e: boolean) => void;
  isCountChanged?: boolean;
}

export default GymDashboardActivity;
